import { useEffect, useMemo, useRef } from 'react';

import type { GroupBase, InputActionMeta, OptionsOrGroups } from 'react-select';

type LoadOptions<Option, Group extends GroupBase<Option>> = (
  inputValue: string,
  callback: (options: OptionsOrGroups<Option, Group>) => void,
) => Promise<OptionsOrGroups<Option, Group>> | void;

type OnInputChange = (newValue: string, actionMeta: InputActionMeta) => void;

export type DebounceProps = { debounceMs?: number };

export function useDebouncedLoadOptions<Option, Group extends GroupBase<Option>>(
  loadOptions: LoadOptions<Option, Group> | undefined,
  onInputChange: OnInputChange | undefined,
  debounceMs: number,
): { loadOptions: LoadOptions<Option, Group> | undefined; onInputChange: OnInputChange | undefined } {
  // Callers pass inline functions, so the debouncer reads the latest ones when it fires instead of being rebuilt each render.
  const latestLoadOptions = useRef(loadOptions);
  latestLoadOptions.current = loadOptions;
  const latestOnInputChange = useRef(onInputChange);
  latestOnInputChange.current = onInputChange;
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => cancelPendingCall(pendingTimer), []);

  const debounced = useMemo(
    () => ({
      loadOptions: ((inputValue, callback) => {
        cancelPendingCall(pendingTimer);

        if (!inputValue) return latestLoadOptions.current?.(inputValue, callback);

        // A cancelled call is never settled: after a cache hit react-select would accept any answer and replace the cached options.
        return new Promise((resolve, reject) => {
          pendingTimer.current = setTimeout(() => {
            pendingTimer.current = null;
            const loader = latestLoadOptions.current;
            if (!loader) return resolve([]);
            try {
              const result = loader(inputValue, resolve);
              if (result) result.then(resolve, reject);
            } catch (error) {
              reject(error);
            }
          }, debounceMs);
        });
      }) satisfies LoadOptions<Option, Group>,
      // A cached input never reaches loadOptions, so the waiting call is cancelled here instead.
      onInputChange: ((newValue, actionMeta) => {
        cancelPendingCall(pendingTimer);
        latestOnInputChange.current?.(newValue, actionMeta);
      }) satisfies OnInputChange,
    }),
    [debounceMs],
  );

  if (debounceMs <= 0 || !loadOptions) return { loadOptions, onInputChange };
  return debounced;
}

function cancelPendingCall(pendingTimer: { current: ReturnType<typeof setTimeout> | null }) {
  if (pendingTimer.current) clearTimeout(pendingTimer.current);
  pendingTimer.current = null;
}
